import {
  AllocatableLoadMethod,
  toAllocatableLoadMethod,
  toTransportCategory,
  transportCategories,
  TransportCategory
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps, NumberInput, SegmentedControl, Textarea, TextInput} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {getTransportCategoryColorRefByType} from "../../../util/transport-category-ui-util";
import {VolumeUnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/VolumeUnitOfMeasureSelect";
import {LinearUnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/LinearUnitOfMeasureSelect";
import {AllocatableLoadMethodSelect} from "../../select/entity/AllocatableLoadMethodSelect/AllocatableLoadMethodSelect";

interface AllocatableLoadSettingsFormProps {
  options?: AllocatableLoadSettingsFormOptions;
  defaultValues?: Partial<AllocatableLoadSettingsFormValues>;
  onSubmit?: (values: AllocatableLoadSettingsFormValues) => void;
  submitting?: boolean;
}

export interface AllocatableLoadSettingsFormOptions {
  // form options here
}

export interface AllocatableLoadSettingsFormValues {
  loadVolume: number
  loadVolumePct?: number
  shortVolume?: number
  volumeUnitOfMeasure: string
  minContainerLevel?: number
  minContainerLactLevel?: number
  maxNonPrimaryLevel?: number
  linearUnitOfMeasure: string
  method: AllocatableLoadMethod
  volumeSpreadFactor?: number
  category: TransportCategory
  description?: string
  displayName?: string
  effectiveDate: Date
}

const getDefaultValues = (defaultValues: Partial<AllocatableLoadSettingsFormValues> | undefined) => {
  return {
    loadVolume: defaultValues?.loadVolume ?? "",
    loadVolumePct: defaultValues?.loadVolumePct ?? "",
    shortVolume: defaultValues?.shortVolume ?? "",
    volumeUnitOfMeasure: defaultValues?.volumeUnitOfMeasure,
    minContainerLevel: defaultValues?.minContainerLevel ?? "",
    minContainerLactLevel: defaultValues?.minContainerLactLevel ?? "",
    maxNonPrimaryLevel: defaultValues?.maxNonPrimaryLevel ?? "",
    linearUnitOfMeasure: defaultValues?.linearUnitOfMeasure,
    method: defaultValues?.method?.code,
    volumeSpreadFactor: defaultValues?.volumeSpreadFactor ?? "",
    category: defaultValues?.category?.code,
    description: defaultValues?.description,
    displayName: defaultValues?.displayName,
    effectiveDate: defaultValues?.effectiveDate,
  }
}

export const AllocatableLoadSettingsForm = (props: AllocatableLoadSettingsFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        loadVolume: Number(values.loadVolume!),
        loadVolumePct: values.loadVolumePct ? Number(values.loadVolumePct) : undefined,
        shortVolume: values.shortVolume ? Number(values.shortVolume) : undefined,
        volumeUnitOfMeasure: values.volumeUnitOfMeasure!,
        minContainerLevel: values.minContainerLevel ? Number(values.minContainerLevel) : undefined,
        minContainerLactLevel: values.minContainerLactLevel ? Number(values.minContainerLactLevel) : undefined,
        maxNonPrimaryLevel: values.maxNonPrimaryLevel ? Number(values.maxNonPrimaryLevel) : undefined,
        linearUnitOfMeasure: values.linearUnitOfMeasure!,
        method: toAllocatableLoadMethod(values.method!),
        volumeSpreadFactor: values.volumeSpreadFactor ? Number(values.volumeSpreadFactor) : undefined,
        category: toTransportCategory(values.category!),
        description: values.description,
        displayName: values.displayName,
        effectiveDate: values.effectiveDate!,
      });
    }
  }, [form, onSubmit]);

  const segmentControlColor = useMemo(() => {
    const formCategory = form.getTransformedValues().category
    if (formCategory) {
      return getTransportCategoryColorRefByType(toTransportCategory(formCategory))
    }
    return undefined
  }, [form])

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <SegmentedControl
        my={"md"}
        color={segmentControlColor}
        data={
          transportCategories.map((c) => {
            return {
              label: t<string>(c.displayValue.id, c.displayValue.defaultMessage),
              value: c.code,
            }
          })}
        {...form.getInputProps("category")} />
      <TextInput
        label={t("common.entity.allocatableloadsettings.form.field.name", "Name")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.name.placeholder", "Name")}
        styles={{wrapper: {width: "100%"}}}
        {...form.getInputProps("displayName")} />
      <Textarea
        label={t("common.entity.allocatableloadsettings.form.field.description", "Description")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.name.description", "Description")}
        autosize={true}
        minRows={4}
        styles={{wrapper: {width: "100%"}}}
        {...form.getInputProps("description")} />
      <Divider my="sm" variant="dotted"/>
      <DateInput
        label={t("common.entity.allocatableloadsettings.form.field.effective", "Effective")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.effective", "Effective")}
        valueFormat="MM/DD/YYYY HH:mm"
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        {...form.getInputProps("effectiveDate")} />
      <Divider my="sm" variant="dotted"/>
      <AllocatableLoadMethodSelect
        label={t("common.entity.allocatableloadsettings.form.field.method", "Method")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.method", "Method")}
        withAsterisk
        required
        {...form.getInputProps("method")} />
      <NumberInput
        label={t("common.entity.allocatableloadsettings.form.field.volumespreadfactor", "Volume Spread Factor")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.volumespreadfactor", "Volume Spread Factor")}
        min={0}
        {...form.getInputProps("volumeSpreadFactor")} />
      <Divider my="sm" variant="dotted"/>
      <NumberInput
        label={t("common.entity.allocatableloadsettings.form.field.loadvolume", "Load Volume")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.loadvolume", "Load Volume")}
        withAsterisk
        required
        {...form.getInputProps("loadVolume")} />
      <NumberInput
        label={t("common.entity.allocatableloadsettings.form.field.loadvolumepct", "Fallback Total Fill Load Trigger %")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.loadvolumepct", "Fallback Total Fill Load Trigger %")}
        min={1}
        max={100}
        {...form.getInputProps("loadVolumePct")} />
      <NumberInput
        label={t("common.entity.allocatableloadsettings.form.field.shortvolume", "Short Volume")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.shortvolume", "Short Volume")}
        {...form.getInputProps("shortVolume")} />
      <VolumeUnitOfMeasureSelect
        label={t("common.entity.allocatableloadsettings.form.field.volumuom", "Volume Units")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.volumuom", "Volume Units")}
        clearable
        withAsterisk
        required
        {...form.getInputProps("volumeUnitOfMeasure")} />
      <Divider my="sm" variant="dotted"/>
      <NumberInput
        label={t("common.entity.allocatableloadsettings.form.field.mincontainerlevel", "Min. Container Level")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.mincontainerlevel", "Min. Container Level")}
        {...form.getInputProps("minContainerLevel")} />
      <NumberInput
        label={t("common.entity.allocatableloadsettings.form.field.mincontainerlactlevel", "Min. Container LACT Level")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.mincontainerlactlevel", "Min. Container LACT Level")}
        {...form.getInputProps("minContainerLactLevel")} />
      <NumberInput
        label={t("common.entity.allocatableloadsettings.form.field.maxnonprimarylevel", "Max. Non-Primary Level")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.maxnonprimarylevel", "Max. Non-Primary Level")}
        {...form.getInputProps("maxNonPrimaryLevel")} />
      <LinearUnitOfMeasureSelect
        label={t("common.entity.allocatableloadsettings.form.field.linearuom", "Linear Units")}
        placeholder={t<string>("common.entity.allocatableloadsettings.form.field.type.linearuom", "Linear Units")}
        clearable
        withAsterisk
        required
        {...form.getInputProps("linearUnitOfMeasure")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface AllocatableLoadSettingsFormModalProps extends AllocatableLoadSettingsFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const AllocatableLoadSettingsFormModal = (props: AllocatableLoadSettingsFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <AllocatableLoadSettingsForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
